import {FaceRecognitionDTO} from 'src/common/models/facial-recognition/FaceRecognitionDTO'
import {LineCrossingDTO} from 'src/common/models/line-crossing/LineCrossingDTO'
import {DetectedFacesFilter} from 'src/utilities/filterBuilder'
import axios from './_axios'
import {prepareParams} from './_internal'
import {AxiosResult, NoPaginate, Ord, Paginate} from './_types'
import {Camera} from './CameraEndpoint'
import {Company} from './CompanyEndpoint'
import {AlarmStatus} from './EventStatusEndpoint'
import {GetLPRParams, Lpr} from './LprEndpoint'

const prefix = process.env.REACT_APP_EVENTS_BASE_URL

export interface DetectionArea {
  id: number
  startX: number
  endX: number
  startY: number
  endY: number
  imageId: number
  accuracy: number
  class: string
  additionalData: {
    boundBoxColor: string
  }
}

export interface SnapshotEvent {
  id: number
  additionDate: string
  awsKey: string
  eventId: number
  demarcatedAreas: DetectionArea[]
  src: string
}

export interface Image {
  id: number
  image: string
  alarm: number
  additionDate: string
  detectionAreas: DetectionArea[]
}

type EventType = {
  id: number
  name: string
}

export interface Alarm {
  id: number
  lastUpdate: string
  createdAt: string
  eventTypeId: number
  statusId: number
  clientId: number
  cameraId: number
  companyId: number
  resolution: string | null
  treated: boolean
  objects: string | null

  camera: Camera
  status: AlarmStatus
  eventType?: EventType
  snapshots?: Array<SnapshotEvent>
  lineCrossingLogs?: Array<LineCrossingDTO>
  faceRecognitions?: Array<FaceRecognitionDTO>
  company?: Company
  activatedActions?: Array<ActivatedActions>
}

export interface FaceDetection {
  id: number
  accuracy: number
  imageKey: string
  cropImageKey: string
  createdAt: string
  cameraId: number
  companyId: number
  face?: Face
  activatedActions: Array<ActivatedActions>
  status?: string
  age?: number
  gender?: string
  angL?: number
  angR?: number
}

export interface ActivatedActions {
  success: boolean
  dispatchTo: string
  reason: string
  actionColor?: string | null
}
export interface Face {
  id: number
  name?: string | null
  cropFaceImageKey: string[]
}

export type FilterEventsType = {
  companyIds?: Array<number>
  eventTypeIds?: Array<string>
  cameraIds?: Array<number>
  statuses?: Array<string>
  statusCameras?: string
  isOnline?: boolean
  from?: string
  to?: string
}

export type SearchEventsType = {
  cameraName?: string
  id?: number
  clientName?: string
}

export type OrderEventsType = {
  ids?: Ord
  timestamp?: Ord
}

export type GetEventsParams = {
  batchSize?: number
  startId: number | null
  includes?: Array<EventRelationsType>
  search?: SearchEventsType
  filter?: FilterEventsType
  orderBy?: OrderEventsType
}

export type OrderedFields = 'createdAt' | 'updatedAt'

type OrderByType = {
  field: OrderedFields
  order: Ord
}

export type GetFaceDetectionsBody = {
  batchSize?: number
  start?: string
  end?: string
  startId?: number
  includes?: Array<EventRelationsType>
  filters: Array<DetectedFacesFilter>
  orderBy?: Array<OrderByType>
}

export type GetFaceDetectionsParams = {
  batchSize?: number
  start?: string
  startId?: number
  end?: string
  includes?: Array<EventRelationsType>
  search?: {
    listName?: string
    faceName?: string
  }
  filter?: {
    id?: number
    faceIds?: Array<number>
    faceName?: string
    companyIds?: Array<number>
  }
  orderBy?: {
    createdAt?: Ord
  }
}

export type GetSingleEventParams = {
  id?: number
  includes?: Array<EventRelationsType>
}

export type EventRelationsType =
  | 'status'
  | 'camera'
  | 'camera.configuration'
  | 'company'
  | 'camera.server'
  | 'camera.client'
  | 'camera.company'
  | 'snapshots'
  | 'snapshots.demarcatedAreas'
  | 'eventType'
  | 'lineCrossingLogs'
  | 'lineCrossingLogs.objectType'
  | 'faceRecognitions'
  | 'faceRecognitions.face'
  | 'viewed'
  | 'faceRecognitions.faceDetectionsHaveActions'
  | 'faceDetectionsHaveActions'
  | 'face'
  | 'event'
  | 'event.status'
  | 'face.faceImages'
  | 'faceRecognitions.face.faceImages'
  | 'activatedActions'

export type GetSnapshotEvent = {
  includes?: string[]
  filter: {
    eventIds: string[]
  }
}

export type SendFeedback = {
  feedback: boolean
  imageId: number
}

export class EventsEndpoint {
  async getMany(params: GetEventsParams): Promise<AxiosResult<NoPaginate<Alarm>>> {
    return await axios.get(`${prefix}`, {params: prepareParams(params)})
  }

  async getFaceDetections(params: GetFaceDetectionsParams): Promise<AxiosResult<NoPaginate<FaceDetection>>> {
    return await axios.get(`${prefix}/face-detections`, {params: prepareParams(params)})
  }
  async getFaceDetectionsWithAdvancedFiltering(body: GetFaceDetectionsBody): Promise<AxiosResult<NoPaginate<FaceDetection>>> {
    return await axios.post(`${prefix}/face-detections`, body)
  }

  async getById(params: GetSingleEventParams): Promise<AxiosResult<Alarm>> {
    return await axios.get(`${prefix}/single/`, {
      params: prepareParams({
        ...params,
      }),
    })
  }

  async getSnapshots(params: GetSnapshotEvent): Promise<AxiosResult<Paginate<SnapshotEvent>>> {
    return await axios.get(`${prefix}/snapshots`, {
      params: prepareParams({
        includes: ['demarcatedAreas'],
        ...params,
      }),
    })
  }

  async getAllLPR(params: GetLPRParams): Promise<AxiosResult<NoPaginate<Lpr>>> {
    const result: AxiosResult<NoPaginate<Lpr>> = await axios.get(`${prefix}/detections`, {
      params: prepareParams(params),
    })
    return result
  }

  async postFeedback(eventId: number, body: SendFeedback): Promise<void> {
    return await axios.post(`${prefix}/feedback/${eventId}`, body)
  }
}
